/**
 * Turns a getUserMedia() failure into a specific, actionable message.
 *
 * A website can never show the native "Allow this site to use your
 * camera?" prompt itself -- only the browser can, and only the very
 * first time a site requests access with no prior decision on record.
 * Once a person (or a browser/OS default) has denied access, browsers
 * stop showing that prompt entirely and silently reject every future
 * request -- which is why a plain "access denied" message is genuinely
 * confusing: there's no visible prompt to retry, so it looks like the
 * tool is just broken.
 *
 * Deliberately does NOT try to pre-check permission state via the
 * Permissions API before calling getUserMedia() -- an earlier version
 * of this file did, and it caused a real regression: in an installed
 * PWA/shortcut context specifically, that pre-check can misreport
 * "denied" even when the OS confirms the app is still allowed to ask,
 * silently skipping the real request before the native prompt ever had
 * a chance to appear. Calling getUserMedia() directly and reacting to
 * its real, actual outcome is slower by one round trip but is the only
 * thing that's actually reliable across contexts.
 */
export function getMediaErrorMessage(err: unknown, kind: "camera" | "microphone"): string {
  const name = err instanceof DOMException ? err.name : err instanceof Error ? err.name : "";
  const label = kind === "camera" ? "Camera" : "Microphone";

  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    if (isStandaloneApp()) {
      // A "standalone" display mode can mean two very different things
      // on Android: a real installed app (WebAPK, with its own entry in
      // Settings > Apps and its own Android-managed permissions), or
      // just a basic "Add to Home Screen" bookmark with the address bar
      // hidden -- which is NOT a real installed app and has no
      // permission entry of its own at all. There's no reliable way to
      // tell which one this is from inside the page. What IS reliable
      // either way: a basic shortcut is really the same underlying
      // browser visiting the same site, so permission granted in a
      // normal browser tab for this same site carries over -- that's
      // the fix that works regardless of which case this is, so it's
      // the one to lead with.
      return `${label} access was denied for this app. Open freedailypro.com in your regular browser app (not ` +
        `this shortcut), tap the lock/info icon by the address bar, set ${label} to Allow there, then come back ` +
        `and try this shortcut again — permission carries over since it's really the same site. If this was ` +
        `installed as a full app, you can also check your phone's Settings -> Apps -> FreeDailyPro -> ` +
        `Permissions -> ${label} directly.`;
    }
    return `${label} access was denied. Browsers only show the "allow ${kind}?" prompt once — to try again, ` +
      `click the lock/info icon in your address bar, set ${label} to Allow, then reload this page.`;
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return `No ${kind} was found on this device.`;
  }
  if (name === "NotReadableError") {
    return `The ${kind} couldn't be started — it may already be in use by another app or browser tab.`;
  }
  return `Couldn't access the ${kind}. Try again.`;
}

/**
 * True when the site is running installed as a standalone app/shortcut
 * (added to home screen) rather than in a normal browser tab with its
 * own address bar -- covers Android's `display-mode: standalone` (what
 * Chrome's "Add to Home Screen" produces) and iOS Safari's older
 * `navigator.standalone` flag. Matters here because the fix for a
 * denied permission is different in each context: a visible address
 * bar to click vs. no address bar at all.
 */
export function isStandaloneApp(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone;
  return window.matchMedia?.("(display-mode: standalone)").matches === true || iosStandalone === true;
}
